import { DATE_FORMATS, formatDate } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CompetencyGroup } from '@/types/review/ReviewDiscussion';

import { CustomPill } from '@/components/CustomPill/CustomPill';
import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { resolveRankedProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { Locale } from '@/constants/Locale';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';

interface HandoutCompetencyGroupsProps {
    competencyGroups: CompetencyGroup[];
    proficiencyLevels: ProficiencyLevel[];
    paginatedGroups: CompetencyGroup[];
    currentPage?: number;
    totalPages?: number;
}

export const HandoutCompetencyGroups = ({
    competencyGroups,
    proficiencyLevels,
    paginatedGroups,
    currentPage,
    totalPages,
}: HandoutCompetencyGroupsProps) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language === Locale.EN ? 'en' : 'fr';
    const orderedLevelIds = orderProficiencyLevels(proficiencyLevels).map((level) => level.id);

    if (competencyGroups.length === 0) {
        return (
            <div
                className={
                    'ds-p-2xl rounded-lg border border-gray-200 bg-white text-center text-gray-500'
                }
            >
                {t('postReviewPrepareMeeting.competencyGroups.empty')}
            </div>
        );
    }

    return (
        <div
            className={'ds-mb-4xl'}
            data-testid={'handout-competency-groups'}
        >
            <h3 className={'text-heading-sm mb-4 font-bold'}>
                {t('postReviewPrepareMeeting.competencyGroups.title')}
            </h3>
            <div className={'space-y-8'}>
                {paginatedGroups.map((group) => {
                    const level = proficiencyLevels.find(
                        (i) => i.id === group.competencies[0]?.proficiencyLevelId,
                    );
                    const groupColor = level?.iconColor ?? '#14b8a6';

                    return (
                        <div
                            key={group.id}
                            className={'overflow-hidden rounded-xl border bg-white'}
                            style={{ borderColor: `${groupColor}40` }}
                        >
                            <div
                                className={
                                    'ds-p-lg flex items-center justify-between border-b border-gray-200'
                                }
                                style={{
                                    background: `linear-gradient(to right, ${groupColor}1f, ${groupColor}0a)`,
                                }}
                            >
                                <h4 className={'text-body-lg font-semibold text-[#26324d]'}>
                                    {group.name}
                                </h4>
                            </div>
                            <ul
                                className={
                                    'max-[600px]:[scrollbar-width:thin] max-[600px]:overflow-x-auto max-[600px]:[&::-webkit-scrollbar]:h-1 max-[600px]:[&::-webkit-scrollbar-thumb]:rounded-full max-[600px]:[&::-webkit-scrollbar-thumb]:bg-gray-300 max-[600px]:[&::-webkit-scrollbar-track]:bg-transparent'
                                }
                            >
                                {group.competencies.map((competency) => {
                                    const level = proficiencyLevels.find(
                                        (i) => i.id === competency?.proficiencyLevelId,
                                    );
                                    const levelColor = level?.iconColor ?? '#14b8a6';
                                    const levelScaleColor = resolveRankedProficiencyLevelColor({
                                        level,
                                        orderedLevelIds,
                                    });

                                    return (
                                        <li
                                            key={competency.id}
                                            className={
                                                'ds-gap-lg ds-px-lg ds-py-lg flex min-h-[56px] items-start border-t border-gray-200 bg-white transition-colors hover:bg-gray-50 max-[600px]:min-w-[420px]'
                                            }
                                        >
                                            <div
                                                className={
                                                    'mt-[2px] flex min-w-[24px] flex-shrink-0 flex-col items-center gap-[6px] pt-[2px]'
                                                }
                                            >
                                                <ProficiencyIcon
                                                    iconEntry={getIconEntry(level?.icon ?? '')}
                                                    color={levelScaleColor}
                                                    iconUrl={level?.iconUrl}
                                                />
                                                {level ? (
                                                    <div className={'flex items-center gap-[2px]'}>
                                                        {proficiencyLevels.map((_, levelIndex) => (
                                                            <span
                                                                key={levelIndex}
                                                                data-testid={'competency-level-bar'}
                                                                className={
                                                                    'h-[4px] w-[10px] rounded-full'
                                                                }
                                                                style={{
                                                                    backgroundColor:
                                                                        levelIndex <
                                                                        competency.assessedScore
                                                                            ? levelScaleColor
                                                                            : '#e2e8f0',
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span
                                                        className={
                                                            'text-caption font-medium text-gray-400'
                                                        }
                                                    >
                                                        {t(
                                                            'postReviewPrepareMeeting.competencyGroups.unrated',
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className={
                                                    'ds-gap-lg flex min-w-0 flex-1 items-center'
                                                }
                                            >
                                                <div className={'min-w-0'}>
                                                    <span
                                                        className={
                                                            'text-body-sm font-semibold ' +
                                                            (competency.isDeactivated ||
                                                            competency.isDeleted
                                                                ? 'text-gray-400 line-through'
                                                                : 'text-navy')
                                                        }
                                                    >
                                                        {competency.name}
                                                    </span>
                                                    {competency.description && (
                                                        <p
                                                            className={
                                                                'ds-mt-xs text-caption text-gray-500'
                                                            }
                                                        >
                                                            {competency.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <CustomPill color={levelColor}>
                                                    {level?.label
                                                        ? getLocalizedText(level.label)
                                                        : t(
                                                              'postReviewPrepareMeeting.competencyGroups.unrated',
                                                          )}
                                                </CustomPill>
                                            </div>
                                            {competency.lastAssessedAt && (
                                                <div className={'flex-shrink-0 text-right'}>
                                                    <p className={'text-caption text-gray-400'}>
                                                        {t(
                                                            'postReviewPrepareMeeting.competencyGroups.evaluationLabel',
                                                        )}
                                                    </p>
                                                    <p
                                                        className={
                                                            'text-caption font-semibold text-gray-700'
                                                        }
                                                    >
                                                        {formatDate({
                                                            date: competency.lastAssessedAt,
                                                            format: DATE_FORMATS.DISPLAY_DATE,
                                                            language: lang,
                                                        })}
                                                    </p>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
            </div>
            {currentPage === totalPages && (
                <div className={'ds-mt-2xl ds-pt-lg border-t border-gray-200'}>
                    <p className={'ds-mb-sm text-caption text-navy font-semibold'}>
                        {t('postReviewPrepareMeeting.proficiencyLegend.title')}
                    </p>
                    <div className={'ds-gap-md text-caption grid grid-cols-2 text-gray-500'}>
                        {proficiencyLevels.map((proficiencyLevel) => (
                            <div
                                key={proficiencyLevel.id}
                                className={'flex items-center gap-[6px]'}
                            >
                                <ProficiencyIcon
                                    iconEntry={getIconEntry(proficiencyLevel.icon)}
                                    color={resolveRankedProficiencyLevelColor({
                                        level: proficiencyLevel,
                                        orderedLevelIds,
                                    })}
                                    iconUrl={proficiencyLevel.iconUrl}
                                    className={'h-[16px] w-[16px]'}
                                />
                                <span>{getLocalizedText(proficiencyLevel.label)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
